(() => {
  if (customElements.get('collection-description')) return;

  class CollectionDescription extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.button = this.querySelector('.collection-header--button');
      this.content = this.querySelector('.collection-header--description');

      if (!this.button || !this.content) return;

      this.onResize = this.handleResize.bind(this);
      this.onToggle = this.toggleDescription.bind(this);
      this.onTransitionEnd = this.handleTransitionEnd.bind(this);

      this.classList.add('init');
      this.button.setAttribute('aria-expanded', 'false');
      window.addEventListener('resize', this.onResize);
      this.button.addEventListener('click', this.onToggle);
      this.content.addEventListener('transitionend', this.onTransitionEnd);
      this.refreshHeights();
      this.initialized = true;
    }

    disconnectedCallback() {
      if (this.onResize) {
        window.removeEventListener('resize', this.onResize);
      }

      if (this.button && this.onToggle) {
        this.button.removeEventListener('click', this.onToggle);
      }

      if (this.content && this.onTransitionEnd) {
        this.content.removeEventListener('transitionend', this.onTransitionEnd);
      }

      window.clearTimeout(this.resizeDebounce);
      window.clearTimeout(this.animationFallback);
      this.initialized = false;
    }

    handleResize() {
      window.clearTimeout(this.resizeDebounce);
      this.resizeDebounce = window.setTimeout(() => this.refreshHeights(), 100);
    }

    refreshHeights() {
      if (!this.content || this.classList.contains('is-animating')) return;

      const collapsedHeight = this.getCollapsedHeight();
      const expandedHeight = this.measureExpandedHeight();
      const hasOverflow = expandedHeight > collapsedHeight + 5;

      this.style.setProperty('--description-collapsed-height', `${collapsedHeight}px`);
      this.style.setProperty('--description-expanded-height', `${expandedHeight}px`);
      this.classList.toggle('active-view-more', hasOverflow);

      if (!hasOverflow) {
        this.classList.remove('active');
        this.button.setAttribute('aria-expanded', 'false');
        this.content.style.height = 'auto';
        return;
      }

      this.content.style.height = this.classList.contains('active') ? 'auto' : `${collapsedHeight}px`;
    }

    getCollapsedHeight() {
      const styles = window.getComputedStyle(this.content);
      const fontSize = parseFloat(styles.fontSize);

      return Number.isFinite(fontSize) ? fontSize * 2.5 : this.content.clientHeight;
    }

    measureExpandedHeight() {
      const previousHeight = this.content.style.height;

      this.classList.add('is-measuring');
      this.content.style.height = 'auto';

      const expandedHeight = this.content.scrollHeight;

      this.content.style.height = previousHeight;
      this.classList.remove('is-measuring');

      return expandedHeight;
    }

    toggleDescription() {
      if (!this.classList.contains('active-view-more')) return;

      window.clearTimeout(this.animationFallback);

      if (this.classList.contains('active')) {
        this.closeDescription();
      } else {
        this.openDescription();
      }
    }

    openDescription() {
      const collapsedHeight = this.getCollapsedHeight();
      const expandedHeight = this.measureExpandedHeight();

      this.style.setProperty('--description-collapsed-height', `${collapsedHeight}px`);
      this.style.setProperty('--description-expanded-height', `${expandedHeight}px`);
      this.classList.add('is-animating');
      this.content.style.height = `${collapsedHeight}px`;

      this.content.offsetHeight;

      this.classList.add('active');
      this.button.setAttribute('aria-expanded', 'true');
      this.content.style.height = `${expandedHeight}px`;
      this.setAnimationFallback();
    }

    closeDescription() {
      const collapsedHeight = this.getCollapsedHeight();
      const expandedHeight = this.content.scrollHeight;

      this.style.setProperty('--description-collapsed-height', `${collapsedHeight}px`);
      this.style.setProperty('--description-expanded-height', `${expandedHeight}px`);
      this.classList.add('is-animating');
      this.content.style.height = `${expandedHeight}px`;

      this.content.offsetHeight;

      this.classList.remove('active');
      this.button.setAttribute('aria-expanded', 'false');
      this.content.style.height = `${collapsedHeight}px`;
      this.setAnimationFallback();
    }

    setAnimationFallback() {
      this.animationFallback = window.setTimeout(() => this.finishAnimation(), 620);
    }

    handleTransitionEnd(event) {
      if (event.target !== this.content || event.propertyName !== 'height') return;

      this.finishAnimation();
    }

    finishAnimation() {
      window.clearTimeout(this.animationFallback);
      this.classList.remove('is-animating');
      this.refreshHeights();
    }
  }

  customElements.define('collection-description', CollectionDescription);
})();
